<template>
    <div
        class="p-px rounded-[20px] md:h-[580px] bg-[linear-gradient(135.39deg,#bdb1ff_7.01%,#6952ed_93.21%)] dark:bg-[linear-gradient(135.39deg,#3840b5_7.01%,#0b0b26_93.21%)]">
        <div
            class="rounded-[19px] overflow-hidden max-md:flex max-md:h-[700px] max-md:flex-col md:h-full md:flex md:flex-col [background:radial-gradient(120%_50%_at_50%_0%,rgba(124,108,246,0.12)_0%,rgba(124,108,246,0)_60%),#ffffff] dark:[background:radial-gradient(120%_50%_at_50%_0%,rgba(124,108,246,0.16)_0%,rgba(124,108,246,0)_60%),#0a0a28]">
            <!-- Demo header -->
            <div class="flex shrink-0 items-center justify-between gap-4 border-b border-[#1E1B4B]/10 dark:border-white/10 px-5 py-3.5 lg:px-6 lg:py-4">
                <div class="flex items-center gap-3">
                    <p class="font-avant-demi m-0 text-[15px] text-[#1E1B4B] dark:text-[#DFE2F1]">Genic<span
                            class="text-[#6D28D9] dark:text-[#D0BCFF]">UI</span></p>
                    <span
                        class="rounded-full border border-[rgba(124,108,246,0.35)] px-2.5 py-[3px] text-[10px] tracking-[0.05em] text-[#5a5478] dark:border-white/15 dark:text-[#c9cdeb]">Playground</span>
                </div>
                <span
                    class="inline-flex items-center gap-[7px] rounded-full bg-[#027100] px-2 py-1 text-xs font-medium text-[#7FFFCB] before:content-[''] before:size-[7px] before:rounded-full before:bg-[#6ee7b7]">Connected</span>
            </div>

            <!-- Chat -->
            <div class="flex min-h-0 flex-1 flex-col space-y-4 px-5 py-4 lg:px-6 lg:py-5">
                <!-- User message (types out) -->
                <div class="flex items-end justify-end gap-2.5 transition-opacity duration-300"
                    :class="userStarted ? 'opacity-100' : 'opacity-0'">
                    <div class="max-w-[75%] sm:max-w-[60%]">
                        <p class="mb-1.5 text-right text-[9px] text-[#5A5478] dark:text-[#8B90B5]">10:42 AM</p>
                        <div
                            class="min-h-[38px] rounded-[14px_14px_4px_14px] bg-[linear-gradient(135deg,#7c5cfc_0%,#5b54e8_100%)] px-3.5 py-2 text-[13px] leading-normal text-white">{{
                                userText }}</div>
                    </div>
                    <span aria-hidden="true"
                        class="grid place-items-center flex-shrink-0 size-7 rounded-full text-[8px] font-semibold tracking-[0.05em] text-white bg-[linear-gradient(135deg,#a78bfa,#7c3aed)]">YOU</span>
                </div>

                <!-- Agent reply (thinks, then streams) -->
                <div class="flex items-start gap-3 transition-opacity duration-300"
                    :class="showAgent ? 'opacity-100' : 'opacity-0'">
                    <span aria-hidden="true"
                        class="grid place-items-center flex-shrink-0 size-8 rounded-full text-xs font-semibold tracking-[0.05em] text-white bg-[linear-gradient(135deg,#4b4fd6,#262b7e)] shadow-[0_0_12px_rgba(124,108,246,0.5)]">A</span>
                    <div class="min-w-0">
                        <p class="flex flex-wrap items-center gap-x-2 text-[13px] font-medium text-[#1E1B4B] dark:text-white">
                            Agent
                            <span class="text-[9px] font-normal tracking-[0.1em] text-[#5A5478] dark:text-[#8B90B5]">{{
                                agentThinking ? "THINKING" : "STREAM CONTINUED" }}</span>
                        </p>
                        <!-- Thinking dots stay in the DOM (opacity-toggled) so their height is always reserved
                             and the card never grows when the reply begins -->
                        <div class="mt-2 flex items-center gap-1.5 transition-opacity duration-300"
                            :class="agentThinking ? 'opacity-100' : 'opacity-0'" aria-hidden="true">
                            <span class="genic-demo-dot size-[5px] rounded-full bg-[#6d28d9] dark:bg-[#8b90b5]"></span>
                            <span class="genic-demo-dot size-[5px] rounded-full bg-[#6d28d9] dark:bg-[#8b90b5]"
                                style="animation-delay: 0.15s"></span>
                            <span class="genic-demo-dot size-[5px] rounded-full bg-[#6d28d9] dark:bg-[#8b90b5]"
                                style="animation-delay: 0.3s"></span>
                        </div>
                        <!-- Reply is always rendered with a reserved line-height so the streaming text
                             never resizes the card -->
                        <p class="mt-1.5 min-h-[21px] text-[13px] leading-relaxed text-[#5A5478] dark:text-[#C9CDEB]">
                            {{ agentText }}<span v-if="typingAgent"
                                class="genic-demo-caret inline-block w-0.5 h-[1em] ml-0.5 align-text-bottom bg-current"
                                aria-hidden="true"></span>
                        </p>
                        <div class="mt-2 flex flex-wrap gap-2 transition-all duration-500"
                            :class="showChips ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'">
                            <span
                                class="rounded-md border border-[rgba(124,108,246,0.35)] bg-[rgba(237,233,254,0.8)] px-2 py-[3px] font-mono text-[10px] text-[#5a5478] dark:bg-[rgba(24,31,74,0.5)] dark:text-[#8b90b5]">{{
                                demo.chip }}</span>
                        </div>
                    </div>
                </div>

                <!-- Workspace Access Control panel (slides in last) -->
                <div
                    class="transition-all duration-500 ease-out flex flex-1 flex-col min-h-0 rounded-[14px] border border-[rgba(124,108,246,0.3)] bg-[rgba(237,233,254,0.6)] px-4 py-3 dark:bg-[rgba(124,108,246,0.07)]"
                    :class="showPanel ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'">
                    <div class="flex flex-wrap items-center justify-between gap-3">
                        <p class="m-0 text-[13px] font-medium text-[#1E1B4B] dark:text-white">{{ demo.panelTitle }}</p>
                        <span
                            class="rounded-full border border-[rgba(124,108,246,0.35)] bg-[rgba(237,233,254,0.8)] px-2.5 py-1 text-[9px] tracking-[0.08em] text-[#5a5478] whitespace-nowrap dark:bg-[rgba(24,31,74,0.5)] dark:text-[#c9cdeb]">{{
                            demo.panelPill }}</span>
                    </div>

                    <!-- Panel body: member table + permission modules in a two-section row -->
                    <div v-if="demo.panel.type === 'table'" class="mt-4 flex min-h-0 flex-1 flex-col gap-2 md:flex-row md:items-start md:gap-4">
                        <!-- Left: member table -->
                        <div class="min-w-0 md:flex-1">
                            <div
                                class="grid grid-cols-[1fr_auto_auto] gap-x-3 border-b border-[#1E1B4B]/10 dark:border-white/10 pb-2 text-[9px] uppercase tracking-[0.08em] text-[#5A5478] dark:text-[#8B90B5]">
                                <p v-for="head in demo.panel.head" :key="head" class="m-0">{{ head }}</p>
                            </div>
                            <div v-for="row in demo.panel.rows" :key="row.name"
                                class="grid grid-cols-[1fr_auto_auto] items-center gap-x-3 border-b border-[#1E1B4B]/10 dark:border-white/5 py-2 last:border-0">
                                <div class="flex min-w-0 items-center gap-2.5">
                                    <span :style="{ background: row.color }"
                                        class="grid place-items-center flex-shrink-0 size-[30px] rounded-full text-[10px] font-semibold text-white"
                                        aria-hidden="true">{{ row.initials }}</span>
                                    <div class="min-w-0">
                                        <p class="m-0 text-[12px] leading-tight text-[#1E1B4B] dark:text-white">{{ row.name }}</p>
                                        <p class="m-0 text-[10px] leading-tight text-[#5A5478] dark:text-[#8B90B5]">{{ row.role }}</p>
                                    </div>
                                </div>
                                <span
                                    class="rounded-full border border-[rgba(124,108,246,0.4)] bg-[rgba(91,84,232,0.12)] px-2.5 py-[3px] text-[10px] text-[#4753dc] dark:bg-[rgba(91,84,232,0.25)] dark:text-white">{{
                                    row.tag }}</span>
                                <span
                                    class="inline-flex items-center gap-[5px] text-[11px] before:content-[''] before:size-1.5 before:rounded-full before:bg-current"
                                    :class="statusColorClasses[row.status]">{{ row.statusLabel }}</span>
                            </div>
                        </div>
                        <!-- Right: permission modules -->
                        <div v-if="demo.modules && demo.modules.length" class="md:w-[168px] md:flex-shrink-0">
                            <p class="mb-2 text-[9px] uppercase tracking-[0.08em] text-[#5A5478] dark:text-[#8B90B5]">{{ demo.modulesLabel
                                }}</p>
                            <div class="space-y-2">
                                <div v-for="module in demo.modules" :key="module.name"
                                    class="flex items-center justify-between gap-2 rounded-[10px] border border-[rgba(124,108,246,0.25)] bg-[rgba(237,233,254,0.6)] px-2.5 py-2 dark:bg-[rgba(24,31,74,0.5)]">
                                    <div class="min-w-0">
                                        <p class="m-0 text-[11px] leading-tight text-[#1E1B4B] dark:text-white">{{ module.name }}</p>
                                        <p class="m-0 text-[9px] leading-tight text-[#5A5478] dark:text-[#8B90B5]">{{ module.hint }}</p>
                                    </div>
                                    <span aria-hidden="true"
                                        class="relative flex-shrink-0 h-[18px] w-[34px] rounded-full border after:content-[''] after:absolute after:top-1/2 after:-translate-y-1/2 after:size-3 after:rounded-full"
                                        :class="module.on ? 'border-[rgba(52,211,153,0.5)] bg-[rgba(52,211,153,0.25)] after:right-[2px] after:bg-[#34d399]' : 'border-[rgba(124,108,246,0.3)] bg-[#e4e1f5] dark:bg-[#2a2f5e] after:left-[2px] after:bg-[#5a5478] dark:after:bg-[#8b90b5]'"></span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="demo.panel.type === 'chart'" class="mt-3 min-h-0 flex-1">
                        <div
                            class="flex h-full w-full flex-col gap-4 rounded-[10px] border border-[rgba(124,108,246,0.25)] bg-[rgba(237,233,254,0.6)] px-3 pt-3 pb-2 md:flex-row md:items-center dark:bg-[rgba(24,31,74,0.5)]">
                            <!-- Left: interactive chart -->
                            <div class="flex min-w-0 flex-col md:flex-1">
                            <!-- Legend -->
                            <div class="mb-1.5 flex shrink-0 items-center justify-between">
                                <p class="m-0 text-[9px] uppercase tracking-[0.08em] text-[#5A5478] dark:text-[#8B90B5]">Portfolio
                                    Value</p>
                                <div class="flex items-center gap-3 text-[9px] text-[#5A5478] dark:text-[#8B90B5]">
                                    <span class="flex items-center gap-1.5">
                                        <span class="inline-block size-1.5 rounded-full bg-[#8B83FF]"></span>Q3 2026
                                    </span>
                                    <span class="flex items-center gap-1.5">
                                        <span
                                            class="inline-block w-3 border-t border-dashed border-[#8B90B5]"></span>Q3
                                        2025
                                    </span>
                                </div>
                            </div>

                            <!-- Interactive chart -->
                            <div class="relative mx-auto w-full max-w-[460px] shrink-0 cursor-crosshair" @mousemove="onChartMove"
                                @mouseleave="onChartLeave">
                                <svg viewBox="0 0 320 96" class="h-auto w-full overflow-visible"
                                    aria-hidden="true">
                                    <defs>
                                        <linearGradient id="genic-chart-fill" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stop-color="rgba(139,131,255,0.35)" />
                                            <stop offset="100%" stop-color="rgba(139,131,255,0)" />
                                        </linearGradient>
                                    </defs>

                                    <!-- Gridlines + Y labels -->
                                    <line x1="0" x2="320" y1="18" y2="18"
                                        class="stroke-[rgba(30,27,75,0.1)] dark:stroke-[rgba(255,255,255,0.06)]" />
                                    <line x1="0" x2="320" y1="44" y2="44"
                                        class="stroke-[rgba(30,27,75,0.1)] dark:stroke-[rgba(255,255,255,0.06)]" />
                                    <line x1="0" x2="320" y1="70" y2="70"
                                        class="stroke-[rgba(30,27,75,0.1)] dark:stroke-[rgba(255,255,255,0.06)]" />
                                    <text x="4" y="14" font-size="8" class="fill-[#5a5478] dark:fill-[#8B90B5]"
                                        opacity="0.8">$1.5M</text>
                                    <text x="4" y="40" font-size="8" class="fill-[#5a5478] dark:fill-[#8B90B5]"
                                        opacity="0.8">$1.35M</text>
                                    <text x="4" y="66" font-size="8" class="fill-[#5a5478] dark:fill-[#8B90B5]"
                                        opacity="0.8">$1.2M</text>

                                    <!-- Previous quarter (dashed) -->
                                    <path
                                        d="M0,88 C15,87 31,87 46,86 C61,85 77,85 92,84 C107,83 123,82 138,80 C153,78 169,79 184,78 C199,77 215,74 230,72 C245,70 261,71 276,70 C291,69 305,66 320,64"
                                        fill="none" stroke="rgba(139,144,181,0.5)" stroke-width="1.5"
                                        stroke-dasharray="4 4" />

                                    <!-- Area + main line -->
                                    <path
                                        d="M0,84 C15,80 31,75 46,73 C61,71 77,79 92,77 C107,75 123,62 138,56 C153,50 169,54 184,51 C199,48 215,38 230,31 C245,24 261,25 276,22 C291,19 305,12 320,7 L320,96 L0,96 Z"
                                        fill="url(#genic-chart-fill)" />
                                    <path
                                        d="M0,84 C15,80 31,75 46,73 C61,71 77,79 92,77 C107,75 123,62 138,56 C153,50 169,54 184,51 C199,48 215,38 230,31 C245,24 261,25 276,22 C291,19 305,12 320,7"
                                        fill="none" stroke="#8B83FF" stroke-width="2" stroke-linecap="round" />

                                    <!-- Data markers -->
                                    <circle v-for="point in chartPoints" :key="point.x" :cx="point.x" :cy="point.y"
                                        r="2.5" fill="#0A0A28" stroke="#8B83FF" stroke-width="1.5" />

                                    <!-- Endpoint + ping -->
                                    <circle cx="320" cy="7" r="3" fill="none" stroke="#8B83FF" stroke-width="1"
                                        class="genic-chart-ping" />
                                    <circle cx="320" cy="7" r="3" fill="#8B83FF" />

                                    <!-- Hover crosshair -->
                                    <g v-if="hoverPoint">
                                        <line :x1="hoverPoint.x" :x2="hoverPoint.x" y1="0" y2="96"
                                            stroke="rgba(139,131,255,0.35)" stroke-dasharray="3 3" />
                                        <circle :cx="hoverPoint.x" :cy="hoverPoint.y" r="3.5" fill="#8B83FF"
                                            stroke="#0A0A28" stroke-width="1.5" />
                                    </g>
                                </svg>

                                <!-- Hover tooltip -->
                                <div v-if="hoverPoint"
                                    class="pointer-events-none absolute z-10 rounded-md border border-[rgba(124,108,246,0.4)] bg-[rgba(255,255,255,0.95)] dark:bg-[rgba(24,31,74,0.95)] px-2 py-1 text-center [transform:translate(-50%,calc(-100%-8px))]"
                                    :style="{ left: hoverPoint.x / 320 * 100 + '%', top: hoverPoint.y / 96 * 100 + '%' }">
                                    <p class="m-0 text-[10px] font-medium leading-tight text-[#1E1B4B] dark:text-white">{{
                                        hoverPoint.value }}</p>
                                    <p class="m-0 text-[8px] leading-tight text-[#5A5478] dark:text-[#8B90B5]">{{ hoverPoint.label
                                        }}</p>
                                </div>
                            </div>

                            <!-- X axis -->
                            <div class="mx-auto mt-1.5 flex w-full max-w-[460px] shrink-0 items-center justify-between text-[9px] text-[#5A5478] dark:text-[#8B90B5]">
                                <span v-for="month in demo.panel.axis" :key="month">{{ month }}</span>
                            </div>
                            </div>

                            <!-- Right: top holdings (stacked) -->
                            <div v-if="demo.panel.holdings && demo.panel.holdings.length"
                                class="md:flex-1 md:border-l md:border-[rgba(124,108,246,0.2)] md:pl-4">
                                <p class="mb-2 text-[9px] uppercase tracking-[0.08em] text-[#5A5478] dark:text-[#8B90B5]">Top
                                    Holdings</p>
                                <div class="space-y-2">
                                    <div v-for="holding in demo.panel.holdings" :key="holding.ticker"
                                        class="flex items-center gap-2.5 rounded-lg border border-[rgba(124,108,246,0.2)] bg-[rgba(237,233,254,0.4)] px-3 py-2 dark:bg-[rgba(24,31,74,0.4)]">
                                        <span
                                            class="size-7 flex-shrink-0 rounded-full bg-[linear-gradient(135deg,#4b4fd6,#262b7e)] dark:bg-[linear-gradient(135deg,#8b5cf6,#5b21b6)] flex items-center justify-center text-[10px] font-medium text-white">
                                            {{ holding.ticker.slice(0, 2) }}</span>
                                        <div class="min-w-0 flex-1">
                                            <p class="m-0 truncate text-[12px] leading-tight text-[#1E1B4B] dark:text-white">{{
                                                holding.name }}</p>
                                            <p class="m-0 text-[10px] leading-tight text-[#5A5478] dark:text-[#8B90B5]">{{
                                                holding.ticker }}</p>
                                        </div>
                                        <span class="flex-shrink-0 text-[12px] font-medium text-[#1E1B4B] dark:text-white">{{
                                            holding.allocation }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="demo.panel.type === 'route'" class="mt-4 min-h-0 flex-1">
                        <div class="flex items-start">
                            <template v-for="(stop, index) in demo.panel.stops" :key="stop.name">
                                <div class="flex w-[72px] flex-shrink-0 flex-col items-center gap-1 text-center">
                                    <span aria-hidden="true"
                                        class="size-2.5 rounded-full border border-[rgba(124,108,246,0.4)] bg-[#e4e1f5] dark:bg-[#2a2f5e]"
                                        :class="routeDotClasses[stop.state]"></span>
                                    <p class="m-0 text-[10px] leading-tight text-[#1E1B4B] dark:text-white">{{ stop.name }}</p>
                                    <p class="m-0 text-[9px] leading-tight text-[#5A5478] dark:text-[#8B90B5]">{{ stop.time }}</p>
                                </div>
                                <span v-if="index < demo.panel.stops.length - 1" aria-hidden="true"
                                    :class="['flex-1 h-px mt-[5px]', index === 0 ? 'bg-[linear-gradient(90deg,#34d399,#8b83ff)]' : 'bg-[rgba(124,108,246,0.3)]']"></span>
                            </template>
                        </div>
                        <p class="mt-3 text-[10px] text-[#5A5478] dark:text-[#8B90B5]">{{ demo.panel.meta }}</p>

                        <!-- Live tracking stats -->
                        <div class="mt-3 grid grid-cols-3 gap-2">
                            <div
                                class="rounded-lg border border-[rgba(124,108,246,0.2)] bg-[rgba(237,233,254,0.5)] px-2.5 py-2 dark:bg-[rgba(24,31,74,0.5)]">
                                <p class="m-0 text-[9px] uppercase tracking-[0.06em] text-[#5A5478] dark:text-[#8B90B5]">Distance
                                    left</p>
                                <p class="m-0 mt-0.5 text-[12px] font-medium text-[#1E1B4B] dark:text-white">12.4 km</p>
                            </div>
                            <div
                                class="rounded-lg border border-[rgba(124,108,246,0.2)] bg-[rgba(237,233,254,0.5)] px-2.5 py-2 dark:bg-[rgba(24,31,74,0.5)]">
                                <p class="m-0 text-[9px] uppercase tracking-[0.06em] text-[#5A5478] dark:text-[#8B90B5]">Avg
                                    speed</p>
                                <p class="m-0 mt-0.5 text-[12px] font-medium text-[#1E1B4B] dark:text-white">62 km/h</p>
                            </div>
                            <div
                                class="rounded-lg border border-[rgba(124,108,246,0.2)] bg-[rgba(237,233,254,0.5)] px-2.5 py-2 dark:bg-[rgba(24,31,74,0.5)]">
                                <p class="m-0 text-[9px] uppercase tracking-[0.06em] text-[#5A5478] dark:text-[#8B90B5]">Vehicles</p>
                                <p class="m-0 mt-0.5 text-[12px] font-medium text-[#1E1B4B] dark:text-white">2 active</p>
                            </div>
                        </div>
                    </div>

                    <!-- Apply row -->
                    <div v-if="demo.applyLabel" class="mt-2.5 shrink-0">
                        <div
                            class="flex flex-wrap items-center gap-2 rounded-[10px] px-3 py-1 pt-3"
                            :class="demo.applyText ? 'justify-between' : 'justify-end'">
                            <p v-if="demo.applyText" class="m-0 text-[11px] text-[#5A5478] dark:text-[#8B90B5]">{{ demo.applyText
                                }}</p>
                            <button type="button"
                                class="inline-flex items-center gap-1.5 cursor-pointer rounded-lg border-0 bg-[linear-gradient(95.99deg,#4753dc_3.69%,#262b7e_96.65%)] px-3.5 py-[7px] text-[11px] text-white transition-[filter] duration-200 hover:brightness-[1.15] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7782ff]">
                                {{ demo.applyLabel }}
                                <WrIcon name="streamline:move-right" class="size-3" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Input bar -->
            <div class="flex shrink-0 items-center gap-3 border-t border-[#1E1B4B]/10 dark:border-white/10 px-5 py-4 lg:px-6">
                <span aria-hidden="true"
                    class="grid place-items-center flex-shrink-0 size-[26px] rounded-full border border-[rgba(124,108,246,0.4)] text-[#5a5478] dark:text-[#8b90b5]">
                    <svg viewBox="0 0 12 12" class="size-3" fill="none" stroke="currentColor" stroke-width="1.5"
                        stroke-linecap="round" aria-hidden="true">
                        <path d="M6 1v10M1 6h10" />
                    </svg>
                </span>
                <p class="m-0 flex-1 text-[12px]">
                    <span v-if="inputText || typingInput" class="text-[#1E1B4B] dark:text-[#DFE2F1]">{{ inputText
                        }}<span v-if="typingInput"
                            class="genic-demo-caret inline-block w-0.5 h-[1em] ml-0.5 align-text-bottom bg-current"
                            aria-hidden="true"></span></span>
                    <span v-else class="text-[#5A5478] dark:text-[#8B90B5]">Add GenicUI anything or command a component...</span>
                </p>
                <button type="button"
                    class="inline-flex items-center gap-1.5 cursor-pointer rounded-lg border-0 bg-[linear-gradient(95.99deg,#4753dc_3.69%,#262b7e_96.65%)] px-3.5 py-2 text-xs text-white transition-[transform_0.15s_ease,filter_0.2s_ease] hover:brightness-[1.15] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7782ff]"
                    :class="sending ? 'scale-[0.94] brightness-[1.25]' : ''">
                    Send
                    <span class="text-[10px] text-white/60" aria-hidden="true">⌘</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
const props = defineProps({
    active: {
        type: Boolean,
        default: true,
    },
    demo: {
        type: Object,
        default: () => ({
            userMessage: "Add 2 team leads to the Admin role.",
            agentMessage: "Role matrix prepared. 2 users updated for approval.",
            chip: "#alpha-team-workshop",
            panelTitle: "Workspace Access Control",
            panelPill: "2 USERS TARGETED",
            panel: {
                type: "table",
                head: ["Member Profile", "Assigned Role", "Status"],
                rows: [
                    {
                        name: "Sarah Chen",
                        role: "Lead Architect",
                        tag: "Admin",
                        initials: "SC",
                        color: "linear-gradient(135deg, #4b4fd6, #262b7e)",
                        status: "stable",
                        statusLabel: "Stable",
                    },
                    {
                        name: "Alex Rivera",
                        role: "Principal Eng",
                        tag: "Admin",
                        initials: "AR",
                        color: "linear-gradient(135deg, #8b5cf6, #5b21b6)",
                        status: "audit",
                        statusLabel: "Audit",
                    },
                ],
            },
            modulesLabel: "Recommended Permission Modules",
            modules: [
                { name: "Billing Access", hint: "Access granted", on: true },
                { name: "API Keys", hint: "Write access", on: true },
            ],
            applyText: "All 2 users confirmed pending attestation.",
            applyLabel: "Apply Changes",
        }),
    },
});

// Status / route-dot color classes (moved from scoped CSS to inline Tailwind)
const statusColorClasses = {
    stable: "text-[#047857] dark:text-[#34d399]",
    audit: "text-[#b45309] dark:text-[#fcd34d]",
    active: "text-[#047857] dark:text-[#34d399]",
};
const routeDotClasses = {
    done: "bg-[#10b981] border-[#10b981] dark:bg-[#34d399] dark:border-[#34d399]",
    active: "bg-[#8b83ff] border-[#8b83ff] shadow-[0_0_10px_rgba(139,131,255,0.8)]",
    upcoming: "",
};

// How long the finished conversation stays on screen before the loop replays
const LOOP_HOLD_MS = 4000;

// Conversation playback state
const userStarted = ref(false);
const userText = ref("");
const inputText = ref("");
const typingInput = ref(false);
const sending = ref(false);
const showAgent = ref(false);
const agentThinking = ref(false);
const agentText = ref("");
const typingAgent = ref(false);
const showChips = ref(false);
const showPanel = ref(false);

let timers = [];

function schedule(fn, delay) {
    timers.push(setTimeout(fn, delay));
}

function resetConversation() {
    timers.forEach(clearTimeout);
    timers = [];
    userStarted.value = false;
    userText.value = "";
    inputText.value = "";
    typingInput.value = false;
    sending.value = false;
    showAgent.value = false;
    agentThinking.value = false;
    agentText.value = "";
    typingAgent.value = false;
    showChips.value = false;
    showPanel.value = false;
}

function typeText(text, target, typing, speed, onDone) {
    typing.value = true;
    let i = 0;
    const step = () => {
        i++;
        target.value = text.slice(0, i);
        if (i < text.length) {
            schedule(step, speed);
        } else {
            typing.value = false;
            onDone && onDone();
        }
    };
    step();
}

function startConversation() {
    resetConversation();

    // Reduced motion: show the full conversation instantly
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        userStarted.value = true;
        userText.value = props.demo.userMessage;
        showAgent.value = true;
        agentText.value = props.demo.agentMessage;
        showChips.value = true;
        showPanel.value = true;
        return;
    }

    // 1. Prompt is typed into the input bar
    schedule(() => {
        typeText(props.demo.userMessage, inputText, typingInput, 32, () => {
            // 2. Send button press
            schedule(() => {
                sending.value = true;
                schedule(() => {
                    // 3. Message lands in the chat, input clears
                    sending.value = false;
                    inputText.value = "";
                    userStarted.value = true;
                    userText.value = props.demo.userMessage;
                    // 4. Agent thinks, then streams the reply
                    schedule(() => {
                        showAgent.value = true;
                        agentThinking.value = true;
                        schedule(() => {
                            agentThinking.value = false;
                            typeText(props.demo.agentMessage, agentText, typingAgent, 14, () => {
                                // 5. Chips, then the workspace panel
                                schedule(() => {
                                    showChips.value = true;
                                }, 250);
                                schedule(() => {
                                    showPanel.value = true;
                                }, 500);
                                // 6. Hold the finished conversation, then replay the whole loop
                                schedule(() => {
                                    startConversation();
                                }, 500 + LOOP_HOLD_MS);
                            });
                        }, 900);
                    }, 500);
                }, 250);
            }, 300);
        });
    }, 400);
}

watch(() => props.active, (isActive) => {
    if (isActive) {
        startConversation();
    } else {
        resetConversation();
    }
});

onMounted(() => {
    if (props.active) {
        startConversation();
    }
});

onUnmounted(() => {
    timers.forEach(clearTimeout);
});

// Chart interactivity (Fintech panel) — x/y map to the 320x96 SVG viewBox
const chartPoints = [
    { x: 0, y: 84, value: "$1.12M", label: "Jul · W1" },
    { x: 46, y: 73, value: "$1.18M", label: "Jul · W2" },
    { x: 92, y: 77, value: "$1.16M", label: "Jul · W3" },
    { x: 138, y: 56, value: "$1.28M", label: "Aug · W1" },
    { x: 184, y: 51, value: "$1.31M", label: "Aug · W2" },
    { x: 230, y: 31, value: "$1.42M", label: "Aug · W3" },
    { x: 276, y: 22, value: "$1.48M", label: "Sep · W1" },
    { x: 320, y: 7, value: "$1.56M", label: "Sep · W2" },
];

const hoverIndex = ref(null);
const hoverPoint = computed(() => (hoverIndex.value === null ? null : chartPoints[hoverIndex.value]));

function onChartMove(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    const index = Math.round(ratio * (chartPoints.length - 1));
    hoverIndex.value = Math.min(chartPoints.length - 1, Math.max(0, index));
}

function onChartLeave() {
    hoverIndex.value = null;
}
</script>

<style scoped>
/* Typing caret */
.genic-demo-caret {
    animation: genic-caret-blink 0.8s step-end infinite;
}

@keyframes genic-caret-blink {
    50% {
        opacity: 0;
    }
}

/* Agent thinking dots */
.genic-demo-dot {
    animation: genic-dot-bounce 1s ease-in-out infinite;
}

@keyframes genic-dot-bounce {
    0%,
    80%,
    100% {
        transform: translateY(0);
        opacity: 0.5;
    }

    40% {
        transform: translateY(-3px);
        opacity: 1;
    }
}

/* Endpoint ping */
@keyframes genic-chart-ping {
    0% {
        r: 3;
        opacity: 0.6;
    }

    100% {
        r: 10;
        opacity: 0;
    }
}

.genic-chart-ping {
    animation: genic-chart-ping 1.6s ease-out infinite;
}

/* Reduced motion: no blink/bounce/ping (JS shows the conversation instantly) */
@media (prefers-reduced-motion: reduce) {
    .genic-demo-caret,
    .genic-demo-dot {
        animation: none;
    }

    .genic-chart-ping {
        animation: none;
        opacity: 0;
    }
}
</style>
